import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import moment from 'moment-timezone';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import API from '../Network/API';

// Teacher moves a booked 1-1 session: new time + reason; the student is emailed by the API.
export default function RescheduleModal({ show, onHide, session, onRescheduled }) {
    const { t } = useTranslation();
    const [time, setTime] = useState('');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    useEffect(() => {
        if (!show) return;
        setTime('');
        setReason('');
        setError('');
    }, [show]);

    const save = (e) => {
        e.preventDefault();
        if (!time || !reason.trim()) {
            setError(t('rescheduleModal.required'));
            return;
        }
        setBusy(true);
        setError('');
        API.post(`/v1/oauth/teacher/enrollments/${session?.id}/reschedule`, {
            start_time: moment(time).format('YYYY-MM-DD HH:mm'),
            timezone,
            reason: reason.trim(),
        })
            .then((response) => {
                const { enrollment, student_notified } = response.data.data;
                if (student_notified) {
                    toast.success(t('rescheduleModal.done'));
                } else {
                    toast.error(t('rescheduleModal.done_no_email'));
                }
                onRescheduled(enrollment);
                onHide();
            })
            .catch((err) => setError(err?.response?.data?.message || t('rescheduleModal.failed')))
            .finally(() => setBusy(false));
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static">
            <form onSubmit={save}>
                <Modal.Header>
                    <h5 className="modal-title">{t('rescheduleModal.title')}</h5>
                    <button type="button" className="btn-close" onClick={onHide} aria-label={t('rescheduleModal.cancel')} />
                </Modal.Header>
                <Modal.Body>
                    {session?.start_time &&
                        <p className="mb-3">
                            {t('rescheduleModal.current_time', { time: moment.tz(session.start_time, 'UTC').local().format('ddd, D MMM YYYY · h:mm A') })}
                        </p>
                    }
                    {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
                    <div className="mb-3">
                        <label className="form-label" htmlFor="reschedule-time">{t('rescheduleModal.new_time')}</label>
                        <input
                            id="reschedule-time"
                            type="datetime-local"
                            className="form-control"
                            min={moment().format('YYYY-MM-DDTHH:mm')}
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                        />
                        <small className="text-muted">{t('rescheduleModal.timezone_hint', { timezone })}</small>
                    </div>
                    <div>
                        <label className="form-label" htmlFor="reschedule-reason">{t('rescheduleModal.reason')}</label>
                        <textarea
                            id="reschedule-reason"
                            className="form-control"
                            rows={3}
                            maxLength={500}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={t('rescheduleModal.reason_placeholder')}
                        />
                        <small className="text-muted">{t('rescheduleModal.reason_hint')}</small>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-light" onClick={onHide} disabled={busy}>{t('rescheduleModal.cancel')}</button>
                    <button type="submit" className="btn btn-primary" disabled={busy}>
                        {busy && <span className="spinner-border spinner-border-sm me-2" />}
                        {t('rescheduleModal.save')}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
